import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Cigarette, Dog, Sparkles, Fuel, Gauge, Clock, User, FileText,
  AlertTriangle, CheckCircle, XCircle, Info, Calendar
} from 'lucide-react';
import { usesNonDefaultTemplate } from '../lib/inspectionTemplates';
import CollapsibleText from './CollapsibleText';

/**
 * VehicleRulesCard Component
 * 
 * Displays vehicle/host rules in a clear, renter-friendly format.
 * 
 * @param {Object} rules - The rules object containing rule values
 * @param {Object} vehicle - Optional vehicle data for vehicle-specific rules
 * @param {boolean} compact - Whether to show compact view
 * @param {string} className - Additional CSS classes
 */
const VehicleRulesCard = ({ 
  rules = {}, 
  vehicle = {},
  compact = false,
  showTitle = true,
  className = '' 
}) => {
  // Category-aware applicability — suppress vehicle-only host rules on any
  // category that uses a non-default checklist template (specialty_asset and
  // the seven new physical categories: motorcycle/trailer/boat/jetski/ebike/
  // electric_scooter/mobility_scooter). Existing `isSpecialty` is preserved
  // so any specialty-only branches downstream keep working.
  const isSpecialty = vehicle.vehicle_category === 'specialty_asset';
  const isNonDefault = usesNonDefaultTemplate(vehicle.vehicle_category);

  // Merge vehicle-level and host-level rules
  const kmsIncluded = vehicle.kms_included || rules.kms_included || 200;
  const excessKmFee = vehicle.excess_km_fee || rules.excess_km_fee || 0.35;
  const fuelPolicy = vehicle.fuel_return_policy || rules.fuel_return_policy || 'same_level';
  const smokingAllowed = rules.smoking_allowed === true;
  const petsAllowed = rules.pets_allowed === true;
  const cleaningRequired = rules.cleaning_required === true;
  const detailingFee = rules.detailing_fee || 0;
  const minimumAge = rules.minimum_driver_age || 21;
  const licenceRestrictions = rules.licence_restrictions || '';
  const additionalRules = rules.additional_rules || rules.custom_rules || '';
  const lateReturnFee = rules.late_return_fee_per_hour || 50;
  const cancellationPolicy = vehicle.cancellation_policy || 'moderate';

  // Format cancellation policy
  const getCancellationText = (policy) => {
    switch(policy) {
      case 'flexible':
        return { label: 'Flexible', desc: 'Full refund up to 24 hours before pickup', color: 'bg-green-100 text-green-700' };
      case 'moderate':
        return { label: 'Moderate', desc: 'Full refund up to 5 days before pickup', color: 'bg-blue-100 text-blue-700' };
      case 'strict':
        return { label: 'Strict', desc: '50% refund up to 7 days before pickup', color: 'bg-amber-100 text-amber-700' };
      default:
        return { label: 'Moderate', desc: 'Full refund up to 5 days before pickup', color: 'bg-blue-100 text-blue-700' };
    }
  };

  const cancellation = getCancellationText(cancellationPolicy);

  // Format fuel policy for display
  const getFuelPolicyText = (policy) => {
    switch(policy) {
      case 'full':
        return 'Return with full tank';
      case 'same_level':
      case 'same':
        return 'Return at same fuel level';
      default:
        return 'Return at same fuel level';
    }
  };

  if (compact) {
    return (
      <div className={`space-y-2 ${className}`} data-testid="vehicle-rules-compact">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4 text-[#004B8D]" />
          <span className="text-sm font-medium text-slate-700">Trip Rules</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="outline" className={`text-xs ${smokingAllowed ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
            <Cigarette className="w-3 h-3 mr-1" />
            {smokingAllowed ? 'Smoking OK' : 'No smoking'}
          </Badge>
          <Badge variant="outline" className={`text-xs ${petsAllowed ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
            <Dog className="w-3 h-3 mr-1" />
            {petsAllowed ? 'Pets OK' : 'No pets'}
          </Badge>
          {!isNonDefault && (
            <>
          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
            <Gauge className="w-3 h-3 mr-1" />
            {kmsIncluded} km/day
          </Badge>
          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
            <Fuel className="w-3 h-3 mr-1" />
            {fuelPolicy === 'full' ? 'Full tank' : 'Same level'}
          </Badge>
            </>
          )}
          {cleaningRequired && (
            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
              <Sparkles className="w-3 h-3 mr-1" />
              Clean return
            </Badge>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card className={`border-slate-100 ${className}`} data-testid="vehicle-rules-card">
      {showTitle && (
        <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
          <CardTitle className="font-heading flex items-center gap-2 text-base sm:text-lg">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-[#004B8D]" />
            Trip Rules
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className={`space-y-4 ${showTitle ? 'p-4 pt-0 sm:p-6 sm:pt-0' : 'p-4 sm:p-6'}`}>
        {/* Primary Rules Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Smoking */}
          <div className={`p-3 rounded-xl flex items-center gap-3 ${smokingAllowed ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${smokingAllowed ? 'bg-green-100' : 'bg-red-100'}`}>
              <Cigarette className={`w-4 h-4 ${smokingAllowed ? 'text-green-600' : 'text-red-600'}`} />
            </div>
            <div>
              <p className={`text-sm font-medium ${smokingAllowed ? 'text-green-800' : 'text-red-800'}`}>
                {smokingAllowed ? 'Smoking permitted' : (isSpecialty ? 'No smoking' : 'No smoking in vehicle')}
              </p>
            </div>
          </div>

          {/* Pets */}
          <div className={`p-3 rounded-xl flex items-center gap-3 ${petsAllowed ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${petsAllowed ? 'bg-green-100' : 'bg-red-100'}`}>
              <Dog className={`w-4 h-4 ${petsAllowed ? 'text-green-600' : 'text-red-600'}`} />
            </div>
            <div>
              <p className={`text-sm font-medium ${petsAllowed ? 'text-green-800' : 'text-red-800'}`}>
                {petsAllowed ? 'Pets allowed' : 'Pets not allowed'}
              </p>
            </div>
          </div>
        </div>

        {/* Kilometre & Fuel Section — vehicle-only; suppressed for any non-default category template */}
        {!isNonDefault && (
          <div className="bg-slate-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <Gauge className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">{kmsIncluded} km included per day</p>
                <p className="text-xs text-slate-500">Excess kilometres charged at ${excessKmFee.toFixed(2)}/km</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <Fuel className="w-4 h-4 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">{getFuelPolicyText(fuelPolicy)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Cleaning Requirement */}
        {cleaningRequired && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-amber-900">
                {isSpecialty ? 'Asset must be returned clean' : 'Vehicle must be returned clean'}
              </p>
              {detailingFee > 0 && (
                <p className="text-xs text-amber-700 mt-1">
                  {isSpecialty ? 'Cleaning fee may apply' : 'Detailing fee may apply'}: ${detailingFee.toFixed(2)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Driver Requirements — vehicle-only; suppressed for any non-default category template */}
        {!isNonDefault && (
          <div className="bg-slate-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
                <User className="w-4 h-4 text-cyan-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">Minimum driver age: {minimumAge} years</p>
              </div>
            </div>
            
            {licenceRestrictions && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">Licence requirement: {licenceRestrictions}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Late Return Fee */}
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
          <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
            <Clock className="w-4 h-4 text-orange-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900">Late return fee: ${lateReturnFee}/hour</p>
          </div>
        </div>

        {/* Cancellation Policy */}
        <div className={`rounded-xl p-4 flex items-start gap-3 ${cancellation.color.replace('text-', 'bg-').replace('700', '50').replace('800', '50')}`} data-testid="cancellation-policy-section">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cancellation.color.replace('text-', 'bg-').replace('700', '100').replace('800', '100')}`}>
            <Calendar className={`w-4 h-4 ${cancellation.color.replace('100', '600')}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-medium text-slate-900">Cancellation Policy</p>
              <Badge className={`text-xs ${cancellation.color}`}>{cancellation.label}</Badge>
            </div>
            <p className="text-xs text-slate-600">{cancellation.desc}</p>
          </div>
        </div>

        {/* Additional Rules */}
        {additionalRules && (
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 mb-1">Additional rules from host:</p>
                <CollapsibleText 
                  text={additionalRules}
                  previewLength={180}
                  showMoreLabel="Show full rules"
                  showLessLabel="Hide rules"
                  textClassName="text-sm text-slate-600"
                />
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * TripRulesSummary Component
 * 
 * A compact summary of key trip rules for booking modals and summaries.
 */
export const TripRulesSummary = ({ 
  rules = {}, 
  vehicle = {},
  className = '' 
}) => {
  const isSpecialty = vehicle.vehicle_category === 'specialty_asset';
  const isNonDefault = usesNonDefaultTemplate(vehicle.vehicle_category);
  const kmsIncluded = vehicle.kms_included || rules.kms_included || 200;
  const excessKmFee = vehicle.excess_km_fee || rules.excess_km_fee || 0.35;
  const fuelPolicy = vehicle.fuel_return_policy || rules.fuel_return_policy || 'same_level';
  const smokingAllowed = rules.smoking_allowed === true;
  const petsAllowed = rules.pets_allowed === true;
  const cleaningRequired = rules.cleaning_required === true;
  const detailingFee = rules.detailing_fee || 0;
  const minimumAge = rules.minimum_driver_age || 21;
  const cancellationPolicy = vehicle.cancellation_policy || 'moderate';

  // Get cancellation policy text
  const getCancellationInfo = (policy) => {
    switch(policy) {
      case 'flexible':
        return { label: 'Flexible', desc: 'Full refund 24h+ before', color: 'text-green-600' };
      case 'strict':
        return { label: 'Strict', desc: '50% refund 7+ days before', color: 'text-amber-600' };
      default:
        return { label: 'Moderate', desc: 'Full refund 5+ days before', color: 'text-blue-600' };
    }
  };

  const cancellation = getCancellationInfo(cancellationPolicy);

  const ruleItems = [
    { 
      icon: smokingAllowed ? CheckCircle : XCircle, 
      text: smokingAllowed ? 'Smoking permitted' : (isSpecialty ? 'No smoking' : 'No smoking in vehicle'),
      color: smokingAllowed ? 'text-green-600' : 'text-red-600'
    },
    { 
      icon: petsAllowed ? CheckCircle : XCircle, 
      text: petsAllowed ? 'Pets allowed' : 'Pets not allowed',
      color: petsAllowed ? 'text-green-600' : 'text-red-600'
    },
  ];
  if (!isNonDefault) {
    ruleItems.push(
      { 
        icon: Gauge, 
        text: `${kmsIncluded} km/day included (${excessKmFee.toFixed(2)}/km excess)`,
        color: 'text-blue-600'
      },
      { 
        icon: Fuel, 
        text: fuelPolicy === 'full' ? 'Return with full tank' : 'Return at same fuel level',
        color: 'text-purple-600'
      }
    );
  }

  if (cleaningRequired) {
    ruleItems.push({
      icon: Sparkles,
      text: detailingFee > 0
        ? (isSpecialty ? `Return clean (fee: $${detailingFee})` : `Return clean (fee: $${detailingFee})`)
        : (isSpecialty ? 'Return asset clean' : 'Return vehicle clean'),
      color: 'text-amber-600'
    });
  }

  if (!isNonDefault) {
    ruleItems.push({
      icon: User,
      text: `Minimum driver age: ${minimumAge}+`,
      color: 'text-cyan-600'
    });
  }

  // Add cancellation policy at the end
  ruleItems.push({
    icon: Calendar,
    text: `Cancellation: ${cancellation.label} - ${cancellation.desc}`,
    color: cancellation.color
  });

  return (
    <div className={`bg-slate-50 rounded-xl p-3 sm:p-4 ${className}`} data-testid="trip-rules-summary">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-[#004B8D]" />
        <span className="text-sm font-medium text-slate-700">Trip Rules</span>
      </div>
      <div className="space-y-2">
        {ruleItems.map((item, index) => {
          const IconComponent = item.icon;
          return (
            <div key={index} className="flex items-center gap-2 text-xs sm:text-sm">
              <IconComponent className={`w-3.5 h-3.5 flex-shrink-0 ${item.color}`} />
              <span className="text-slate-700">{item.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VehicleRulesCard;
